import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, Download, Users, DollarSign, TrendingUp, BookOpen, Filter, Calendar } from 'lucide-react';
import { salesService } from '../services/api';
import { format } from 'date-fns';

export default function Sales() {
  const [filters, setFilters] = useState({
    search: '',
    date_from: '',
    date_to: ''
  });

  const { data: salesData, isLoading } = useQuery({
    queryKey: ['sales', filters],
    queryFn: () => salesService.getPurchases(filters)
  });

  const { data: stats } = useQuery({
    queryKey: ['sales-stats'],
    queryFn: salesService.getStats
  });

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1d1d1d]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Sales Analytics</h1>
          <p className="text-gray-600 font-medium">
            Track sales and revenue across the platform
          </p>
        </div>
        <button className="bg-[#1d1d1d] text-white px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-gray-800 transition-all duration-200 shadow-md hover:shadow-lg flex items-center">
          <Download className="h-4 w-4 mr-2" />
          Export Report
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-2xl shadow-xs border border-gray-200 hover:shadow-md transition-all duration-300">
          <div className="flex items-center">
            <div className="p-3 rounded-xl bg-green-100">
              <DollarSign className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-semibold text-gray-600">Total Revenue</p>
              <p className="text-2xl font-bold text-gray-900">
                GHS {stats?.total_revenue || '0.00'}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-2xl shadow-xs border border-gray-200 hover:shadow-md transition-all duration-300">
          <div className="flex items-center">
            <div className="p-3 rounded-xl bg-[#1d1d1d]">
              <TrendingUp className="h-6 w-6 text-white" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-semibold text-gray-600">Platform Earnings</p>
              <p className="text-2xl font-bold text-gray-900">
                GHS {stats?.platform_earnings || '0.00'}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-2xl shadow-xs border border-gray-200 hover:shadow-md transition-all duration-300">
          <div className="flex items-center">
            <div className="p-3 rounded-xl bg-purple-100">
              <BookOpen className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-semibold text-gray-600">Total Sales</p>
              <p className="text-2xl font-bold text-gray-900">
                {stats?.total_sales || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-2xl shadow-xs border border-gray-200 hover:shadow-md transition-all duration-300">
          <div className="flex items-center">
            <div className="p-3 rounded-xl bg-yellow-100">
              <Users className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-semibold text-gray-600">Active Authors</p>
              <p className="text-2xl font-bold text-gray-900">
                {stats?.active_authors || 0}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl shadow-xs border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <Filter className="h-5 w-5 mr-2 text-[#1d1d1d]" />
            Filters
          </h3>
          <button
            onClick={() => setFilters({ search: '', date_from: '', date_to: '' })}
            className="text-sm text-gray-500 hover:text-gray-700 font-medium transition-colors duration-200"
          >
            Clear all
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Search
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search purchases..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="pl-10 w-full rounded-xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-all duration-200"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              From Date
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="date"
                value={filters.date_from}
                onChange={(e) => handleFilterChange('date_from', e.target.value)}
                className="pl-10 w-full rounded-xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-all duration-200"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              To Date
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="date"
                value={filters.date_to}
                onChange={(e) => handleFilterChange('date_to', e.target.value)}
                className="pl-10 w-full rounded-xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-all duration-200"
              />
            </div>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => setFilters({ search: '', date_from: '', date_to: '' })}
              className="w-full bg-gray-100 text-gray-700 px-4 py-3 rounded-xl hover:bg-gray-200 transition-all duration-200 font-semibold"
            >
              Reset Filters
            </button>
          </div>
        </div>
      </div>

      {/* Sales Table */}
      <div className="bg-white rounded-2xl shadow-xs border border-gray-200 overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              Recent Purchases ({salesData?.results?.length || 0})
            </h2>
            <div className="text-sm text-gray-500 font-medium">
              Sorted by: Latest
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Book & Reader
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Author
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Transaction ID
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {salesData?.results?.map((purchase) => (
                <tr key={purchase.id} className="hover:bg-gray-50 transition-colors duration-200">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <img
                        src={purchase.book_cover}
                        alt={purchase.book_title}
                        className="h-12 w-9 object-cover rounded-lg mr-4 border border-gray-200"
                      />
                      <div>
                        <div className="text-sm font-semibold text-gray-900">
                          {purchase.book_title}
                        </div>
                        <div className="text-sm text-gray-600 font-medium">
                          {purchase.reader_name}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-semibold text-gray-900">{purchase.author_name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-bold text-green-600">
                      GHS {purchase.amount_paid}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 font-medium">
                    {format(new Date(purchase.purchase_date), 'MMM dd, yyyy')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 font-medium">
                    {purchase.transaction_id}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {(!salesData?.results || salesData.results.length === 0) && (
          <div className="text-center py-16">
            <DollarSign className="mx-auto h-16 w-16 text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No sales found</h3>
            <p className="text-gray-600 font-medium mb-4">
              Try adjusting your search or filter criteria.
            </p>
            <button
              onClick={() => setFilters({ search: '', date_from: '', date_to: '' })}
              className="bg-[#1d1d1d] text-white px-6 py-3 rounded-xl text-sm font-semibold hover:bg-gray-800 transition-all duration-200"
            >
              Clear all filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
}